import { Box, Grid, Typography } from "@mui/material";
import { IntegrationType, useGetIntegrationsSummaryQuery } from "~/operations";
import { Loading } from "~/components/loading";
import { motion } from "motion/react";
import {
  securityPipelineIntegrationTypes,
  thirdPartyImportIntegrationTypes,
  ticketingIntegrationTypes,
} from "~/pages/integrations/utils/integrationConstants";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";

type DataBoxCounts = {
  total: number;
  assets?: number;
  policies?: number;
  checks?: number;
};

interface IntegrationHeaderDataboxProps {
  scope: SpaceScope | OrganizationScope;
  types: IntegrationType[];
}

export function IntegrationHeaderDatabox({
  scope,
  types,
}: IntegrationHeaderDataboxProps) {
  const { data, error, loading } = useGetIntegrationsSummaryQuery({
    variables: { input: { spaceMrn: scope.mrn } },
    skip: scope.type !== ScopeType.Space,
  });

  //If we error, lets just not show anything
  if (error) {
    return <div />;
  }

  if (loading || !data?.integrationsSummary.integrationSummaries) {
    return (
      <Box sx={[{ mt: 0.8 }, { "& > *": { height: "unset" } }]}>
        <Loading what="data counts" />
      </Box>
    );
  }

  const summaries = data.integrationsSummary.integrationSummaries.filter(
    (summary) => types.includes(summary.type),
  );

  const dataBoxCounts: DataBoxCounts = summaries.reduce(
    (acc, curr) => {
      acc.total = acc.total + curr.totalCount;
      acc.assets = acc.assets + curr.assetsCount;
      acc.policies = acc.policies + curr.policiesCount;
      acc.checks = acc.checks + curr.checksCount;
      return acc;
    },
    {
      total: 0,
      assets: 0,
      policies: 0,
      checks: 0,
    },
  );

  // If we have a ticketing system, we should only show the total count
  if (
    types.some((type) =>
      [
        ...ticketingIntegrationTypes,
        ...securityPipelineIntegrationTypes,
      ].includes(type),
    )
  ) {
    delete dataBoxCounts.assets;
    delete dataBoxCounts.policies;
    delete dataBoxCounts.checks;
  }

  if (types.some((type) => thirdPartyImportIntegrationTypes.includes(type))) {
    delete dataBoxCounts.policies;
    delete dataBoxCounts.checks;
  }

  return (
    <Grid
      component={motion.div}
      container
      columnSpacing={2}
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "flex-end",
        my: {
          xs: 2,
          sm: 0,
        },
      }}
      size={{
        xs: 12,
        sm: "grow",
      }}
    >
      {Object.entries(dataBoxCounts).map(([key, value]) => {
        return (
          <Grid key={key} sx={{ textAlign: "center" }}>
            <Typography
              fontSize={11}
              lineHeight="16px"
              color="text.secondary"
              sx={{ textTransform: "uppercase", mb: 2 }}
            >
              {key}
            </Typography>
            <Typography
              className={"integrations-value"}
              fontSize={24}
              lineHeight="15px"
              fontWeight={800}
            >
              {value.toLocaleString()}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
}
